import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useMe } from '../hooks/useMe';
import { useTeam } from '../context/TeamContext';
import * as templatesApi from '../api/templates';
import { ScheduleDaysPicker } from './ScheduleDaysPicker';

interface Props {
  open: boolean;
  /** Pre-select this group if the caller opened the modal from a group view. */
  defaultGroupId?: string;
  onClose: () => void;
  onDone: (toastMessage: string) => Promise<void> | void;
}

/**
 * Create a new recurring template (ADR 0010). Any authenticated user can
 * create a template in any group they're a member of. Checklist items are
 * edited inline — enter the label, press + (or Enter).
 */
export function CreateTemplateModal({
  open,
  defaultGroupId,
  onClose,
  onDone,
}: Props) {
  const { me } = useMe();
  const { members } = useTeam();
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [groupId, setGroupId] = useState<string>('');
  const [labels, setLabels] = useState<string[]>([]);
  const [labelDraft, setLabelDraft] = useState('');
  const [scheduleDays, setScheduleDays] = useState<number[]>([]);
  const [defaultAssigneeSub, setDefaultAssigneeSub] = useState<string>('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const groups = useMemo(() => {
    const gs = me?.groups ?? [];
    return [...gs].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [me]);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setGroupId(defaultGroupId && groups.some((g) => g.id === defaultGroupId)
      ? defaultGroupId
      : groups[0]?.id ?? '');
    setLabels([]);
    setLabelDraft('');
    setScheduleDays([]);
    setDefaultAssigneeSub('');
    setActive(true);
    setErr(null);
    setTimeout(() => firstFieldRef.current?.focus(), 0);
  }, [open, defaultGroupId, groups]);

  const canSubmit = title.trim().length > 0 && groupId && !busy;

  if (!open) return null;

  function addLabel() {
    const v = labelDraft.trim();
    if (!v) return;
    setLabels((arr) => [...arr, v]);
    setLabelDraft('');
  }

  function removeLabel(idx: number) {
    setLabels((arr) => arr.filter((_, i) => i !== idx));
  }

  function updateLabel(idx: number, value: string) {
    setLabels((arr) => arr.map((v, i) => (i === idx ? value : v)));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await templatesApi.createTemplate({
        groupId,
        title: title.trim(),
        description: description.trim() || undefined,
        checklistItems: labels
          .map((l) => l.trim())
          .filter((l) => l.length > 0)
          .map((l) => ({ label: l })),
        scheduleDays,
        defaultAssigneeSub: defaultAssigneeSub || undefined,
        active,
      });
      await onDone(`Created template "${title.trim()}"`);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="new-task-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Create template"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <form
        className="new-task-dialog settings-modal template-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <header>
          <h3>New template</h3>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>

        <label>
          <span>Title</span>
          <input
            ref={firstFieldRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Monthly commissions"
            required
          />
        </label>

        <label>
          <span>Description (optional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Context or instructions"
            rows={2}
          />
        </label>

        <div className="new-task-grid">
          {groups.length >= 2 && (
            <label>
              <span>Group</span>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                required
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}
          {members.length > 1 && (
            <label>
              <span>Default assignee</span>
              <select
                value={defaultAssigneeSub}
                onChange={(e) => setDefaultAssigneeSub(e.target.value)}
              >
                <option value="">(me — creator)</option>
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <fieldset className="template-checklist-fieldset">
          <legend>Checklist items</legend>
          {labels.length === 0 && (
            <p className="muted small">
              Add one row per item (e.g. one per carrier).
            </p>
          )}
          {labels.map((label, i) => (
            <div key={i} className="template-checklist-row">
              <input
                type="text"
                value={label}
                onChange={(e) => updateLabel(i, e.target.value)}
                placeholder="Item label"
              />
              <button
                type="button"
                className="ghost small"
                onClick={() => removeLabel(i)}
                aria-label="Remove item"
              >
                ×
              </button>
            </div>
          ))}
          <div className="template-checklist-row">
            <input
              type="text"
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addLabel();
                }
              }}
              placeholder="New item…"
            />
            <button
              type="button"
              onClick={addLabel}
              disabled={!labelDraft.trim()}
            >
              + Add
            </button>
          </div>
        </fieldset>

        <fieldset className="template-schedule-fieldset">
          <legend>Schedule days</legend>
          <p className="muted small">
            Days of the month the template fires. Empty = manual only.
          </p>
          <ScheduleDaysPicker
            value={scheduleDays}
            onChange={setScheduleDays}
          />
        </fieldset>

        <label className="new-task-private-row">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          <span>Active (scheduler will fire this template)</span>
        </label>

        {err && <div className="settings-error">{err}</div>}

        <div className="new-task-footer">
          <span className="muted small">
            {labels.filter((l) => l.trim()).length} checklist item(s)
          </span>
          <button type="submit" className="primary" disabled={!canSubmit}>
            {busy ? 'Creating…' : 'Create template'}
          </button>
        </div>
      </form>
    </div>
  );
}
